import { useMemo } from 'react';

import type { AnyRoute } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';

import { pageNameDict, PageNames, siteServiceForPage } from '@/constants';
import { useSiteServiceSupport } from '@/hooks';
import { useConfigStore } from '@/stores';
import { isPlaygroundInSidebar } from '@/utils';

import type { navLinkType } from './baseLayout.types.ts';
import { getIconByName } from './getIconByName.ts';

export const useGetRouteTreeGroups = (routeTree: AnyRoute) => {
  const { portableMode } = useConfigStore();
  const siteServiceSupport = useSiteServiceSupport();

  const { dashboard, utils, services } = useMemo(() => getRouteGroups(routeTree), [routeTree]);

  const sortedDashboard = useMemo(
    () => [...dashboard].sort((a, b) => getSortPriority(a) - getSortPriority(b)),
    [dashboard]
  );

  const filteredServices = useMemo(
    () =>
      services
        .filter(item => {
          const service = siteServiceForPage(routeKeyFromPath(item.fullPath));
          if (!service) return false;
          // Keep the item hidden until the lookup settles so it is only ever revealed once, when a
          // reachable site has the device.
          const { hasSite, isInitialLoading } = siteServiceSupport[service];
          return !isInitialLoading && hasSite;
        })
        .map(item => {
          const service = siteServiceForPage(routeKeyFromPath(item.fullPath));
          if (!service) return item;
          // Mark services the current site does not support. They stay listed and clickable so
          // the route can offer a site picker.
          return {
            ...item,
            service,
            disabled: !siteServiceSupport[service].currentSiteIsSupported,
          };
        }),
    [services, siteServiceSupport]
  );

  const sortedFilteredServices = useMemo(
    () => [...filteredServices].sort((a, b) => getSortPriority(a) - getSortPriority(b)),
    [filteredServices]
  );

  const filteredUtils = useMemo(
    () =>
      utils.filter(item => {
        const navKey = routeKeyFromPath(item.fullPath);
        if (navKey === 'playground') return isPlaygroundInSidebar();
        // The setting is stored in cloud inventory, which only portable mode talks to.
        if (navKey === PageNames.SiteSetup) return Boolean(portableMode);
        const service = siteServiceForPage(navKey);
        if (!service) return true;
        const { hasSite, isInitialLoading } = siteServiceSupport[service];
        return !isInitialLoading && hasSite;
      }),
    [utils, portableMode, siteServiceSupport]
  );

  const orderedUtils = useMemo(
    () => [...filteredUtils].sort((a, b) => utilsOrder(a) - utilsOrder(b)),
    [filteredUtils]
  );

  return useMemo(
    () => ({
      dashboard: sortedDashboard,
      utils: orderedUtils,
      services: sortedFilteredServices,
    }),
    [sortedDashboard, orderedUtils, sortedFilteredServices]
  );
};

/** Normalize to the last path segment (lowercase, trimmed). */
const routeKeyFromPath = (p: string): string => {
  if (!p) return '';
  const { pathname } = new URL(p, 'http://dummy');
  const cleaned = pathname.replace(/\/+$/, '');
  const last = cleaned.split('/').filter(Boolean).pop() ?? '';
  return last.toLowerCase();
};

const buildRouteTree = (node: AnyRoute): navLinkType | null => {
  if (!node || node.id === '__root__') return null;

  const id = node.id ?? '(anonymous)';
  const path = node.path;
  const fullPath = node.fullPath ?? '';
  const nameKey = fullPath === '/' ? PageNames.Dashboard : routeKeyFromPath(fullPath);
  const productName = pageNameDict[nameKey as PageNames] ?? (node.path as ParseKeys);
  const icon = getIconByName(nameKey as PageNames);

  const children =
    (node.children ?? []).map((child: AnyRoute) => buildRouteTree(child)).filter(isNonNull) ?? [];

  return { id, fullPath, icon, children, pageName: productName, path };
};

const getRouteGroups = (root: AnyRoute) => {
  const dashboard: navLinkType[] = [];
  const utils: navLinkType[] = [];
  const services: navLinkType[] = [];

  for (const child of root.children ?? []) {
    // Route groups (e.g. (dashboard), (app)) are layout wrappers
    if (!child.id || !child.id.startsWith('/(') || !child.id.endsWith(')')) {
      continue;
    }
    const groupId = child.id;
    const groupChildren = (child.children ?? [])
      .map((c: AnyRoute) => buildRouteTree(c))
      .filter(isNonNull);

    if (groupId === '/(dashboard)') {
      dashboard.push(...groupChildren);
    } else {
      // (app) or other groups: split by "utils" segment
      for (const n of groupChildren) {
        const key = routeKeyFromPath(n.fullPath);
        if (key === 'utils') {
          utils.push(...n.children);
        } else {
          services.push(n);
        }
      }
    }
  }

  return { dashboard, utils, services };
};

/** Type guard for non-null values. */
const isNonNull = <T>(x: T | null | undefined): x is T => x != null;

/**
 * Determines the sort priority for a given navigation link.
 *
 * @param {navLinkType} item - The navigation link object to be evaluated for sort priority.
 * @returns {number} The sort priority of the navigation link. Returns:
 *                   0 if the link's fullPath is "/",
 *                   1 if the link is the Layouts route,
 *                   2 for all other cases.
 */
const getSortPriority = (item: navLinkType): number => {
  if (item.fullPath === '/') return 0;
  if (routeKeyFromPath(item.fullPath) === PageNames.Layouts) return 1;
  return 2;
};

/** Order of the utils group in the sidebar. Routes not listed go last. */
const UTILS_ORDER: readonly string[] = [
  PageNames.Automation,
  PageNames.CameraCheck,
  PageNames.DeviceConfig,
  PageNames.EventStream,
  PageNames.Inventory,
  PageNames.NetworkHealth,
  'playground',
  PageNames.SiteSetup,
  PageNames.Settings,
];

const utilsOrder = (item: navLinkType): number => {
  const index = UTILS_ORDER.indexOf(routeKeyFromPath(item.fullPath));
  return index === -1 ? UTILS_ORDER.length : index;
};
